import {
	BackgroundAttributes,
	BackgroundControl,
	ButtonAttributes,
	ButtonControl,
	SpacingAttributes,
	SpacingControl,
	TypographyAttributes,
	TypographyControl,
} from '@redfox-thank-you/control-groups';
import { BaseControl, ButtonGroup } from '@redfox-thank-you/controls';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { headingLevelOptions } from '../constants';
import type { SettingsProps } from './types';

/**
 * Settings component for Downloads block
 */
export const DownloadsSettings: React.FC< SettingsProps > = ( {
	updateAttributes,
	getAttribute,
} ) => {
	// Helper functions for specific attribute types
	const updateTypography = (
		section: 'title' | 'items' | 'blockText',
		typographyAttributes: Partial< TypographyAttributes >,
	) => {
		const stylePath =
			section === 'blockText' ? 'style.block.text' : `style.${ section }`;
		updateAttributes( stylePath, typographyAttributes );
	};

	const updateBackground = (
		backgroundAttributes: Partial< BackgroundAttributes >,
	) => {
		updateAttributes( 'style.block.background', backgroundAttributes );
	};

	const updateSpacing = (
		spacingAttributes: Partial< SpacingAttributes >,
	) => {
		updateAttributes( 'style.block.spacing', spacingAttributes );
	};

	const updateButton = ( buttonAttributes: Partial< ButtonAttributes > ) => {
		updateAttributes( 'style.downloadButton', buttonAttributes );
	};

	// Extract style properties with defaults
	const blockTextStyle = getAttribute< TypographyAttributes >(
		'style.block.text',
		{},
	);
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
		{},
	);
	const blockSpacingStyle = getAttribute< SpacingAttributes >(
		'style.block.spacing',
		{},
	);
	const titleStyle = getAttribute< TypographyAttributes >(
		'style.title',
		{},
	);
	const itemsStyle = getAttribute< TypographyAttributes >(
		'style.items',
		{},
	);
	const downloadButtonStyle = getAttribute< ButtonAttributes >(
		'style.downloadButton',
		{},
	);

	const showTitle = getAttribute( 'settings.title.show', true );
	const titleLevel = getAttribute( 'settings.title.level', 2 );
	const showDownloadCount = getAttribute(
		'settings.display.downloadCount',
		true,
	);

	return (
		<>
			<InspectorControls>
				{ /* Title Settings */ }
				<PanelBody
					title={ __( 'Title Settings', 'redfox-thank-you' ) }
					initialOpen={ true }
				>
					<BaseControl
						id="show-title"
						label={ __( 'Show Title', 'redfox-thank-you' ) }
						help={ __(
							'Display or hide the block title.',
							'redfox-thank-you',
						) }
						inline
						helpAsTooltip
					>
						<ToggleControl
							label=""
							checked={ showTitle }
							onChange={ ( value: boolean ) =>
								updateAttributes( 'settings.title.show', value )
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>

					{ showTitle && (
						<>
							<BaseControl
								id="heading-level"
								label={ __(
									'Heading Level',
									'redfox-thank-you',
								) }
								help={ __(
									'Choose the semantic heading level for SEO and accessibility.',
									'redfox-thank-you',
								) }
							>
								<ButtonGroup
									value={ titleLevel.toString() }
									options={ headingLevelOptions }
									onChange={ ( value ) =>
										updateAttributes(
											'settings.title.level',
											parseInt( value || '2' ),
										)
									}
									allowDeselect={ false }
								/>
							</BaseControl>
						</>
					) }
				</PanelBody>

				{ /* Display Settings */ }
				<PanelBody
					title={ __( 'Display Options', 'redfox-thank-you' ) }
					initialOpen={ false }
				>
					<BaseControl
						id="show-download-count"
						label={ __(
							'Show download count',
							'redfox-thank-you',
						) }
						help={ __(
							'Display the remaining download count for each item.',
							'redfox-thank-you',
						) }
						inline
					>
						<ToggleControl
							label=""
							checked={ showDownloadCount }
							onChange={ ( downloadCount: boolean ) =>
								updateAttributes(
									'settings.display.downloadCount',
									downloadCount,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
				</PanelBody>
			</InspectorControls>

			<InspectorControls group="styles">
				<TypographyControl
					title={ __( 'Text', 'redfox-thank-you' ) }
					initialOpen={ true }
					attributes={ blockTextStyle }
					setAttributes={ ( attrs ) =>
						updateTypography( 'blockText', attrs )
					}
				/>

				{ showTitle && (
					<TypographyControl
						title={ __( 'Title Text', 'redfox-thank-you' ) }
						initialOpen={ false }
						attributes={ titleStyle }
						setAttributes={ ( attrs ) =>
							updateTypography( 'title', attrs )
						}
						enabledControls={ {
							wordSpacing: true,
						} }
					/>
				) }

				<TypographyControl
					title={ __(
						'Download Items Title Text',
						'redfox-thank-you',
					) }
					initialOpen={ false }
					attributes={ itemsStyle }
					setAttributes={ ( attrs ) =>
						updateTypography( 'items', attrs )
					}
				/>

				<ButtonControl
					title={ __( 'Download Button', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ downloadButtonStyle }
					setAttributes={ updateButton }
					enabledControls={ {
						backgroundColor: true,
						textColor: true,
						responsive: true,
					} }
				/>

				<BackgroundControl
					title={ __( 'Background', 'redfox-thank-you' ) }
					initialOpen={ false }
					responsive={ true }
					attributes={ blockBackgroundStyle }
					setAttributes={ updateBackground }
					enabledControls={ {
						backgroundColor: true,
						backgroundImage: true,
						backgroundGradient: true,
						backgroundPattern: false,
						backgroundOverlay: true,
						advancedOptions: true,
						responsive: true,
					} }
				/>

				<SpacingControl
					title={ __( 'Spacing', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ blockSpacingStyle }
					setAttributes={ updateSpacing }
					enabledControls={ {
						padding: true,
						margin: true,
						responsive: true,
						presets: true,
						boxModel: true,
						autoValues: true,
					} }
					units={ [ 'px', 'em', 'rem', '%' ] }
					min={ 0 }
					max={ 200 }
					allowReset={ true }
				/>
			</InspectorControls>
		</>
	);
};
